// src/pages/gene.codes/GeneticWheel/OrganExpression/OrganExpressionVisualization.tsx
import React, { useEffect, useState, useRef } from "react";
import { Divider, Skeleton, Alert, Typography } from "antd";
import { fetchGeneOrganExpressions, fetchBodySvgs, fetchCellularLocations } from "../../../../api/geneCodes/GeneExpressionApi";
import { OrganExpressionVisualizationProps, FormattedOrganExpression, CellularLocation } from "../../../../types/geneCodes/GeneExpressionTypes";
import { organParse, locationParse } from "../../../../utils/geneCodes/OrganVisualizationUtils";
import "./OrganExpressionVisualization.css";

const { Text } = Typography;

/**
 * Component for visualizing gene expression in different organs and cell locations
 */
const OrganExpressionVisualization: React.FC<OrganExpressionVisualizationProps> = ({ geneName, className = "" }) => {
	// State for organ expression data
	const [organExpressions, setOrganExpressions] = useState<FormattedOrganExpression[]>([]);
	const [cellularLocations, setCellularLocations] = useState<CellularLocation[]>([]);

	// State for SVG content
	const [svgContent, setSvgContent] = useState<{ male: string; female: string; cell: string }>({
		male: "",
		female: "",
		cell: "",
	});

	// State for active organ and loading/error states
	const [activeOrgan, setActiveOrgan] = useState<string | null>(null);
	const [activeLocation, setActiveLocation] = useState<string | null>(null);
	const [loading, setLoading] = useState<boolean>(true);
	const [error, setError] = useState<string | null>(null);

	// Sex determined by backend data (default to showing male only)
	const [sex, setSex] = useState<"M" | "F" | "null">("M");

	// Refs for containers (needed for DOM manipulation of SVG content)
	const organLevelsRef = useRef<HTMLDivElement>(null);

	// Fetch data when geneName changes
	useEffect(() => {
		// Skip if no gene name is provided
		if (!geneName) {
			setLoading(false);
			return;
		}

		const fetchData = async () => {
			setLoading(true);
			setError(null);

			try {
				// Fetch SVGs first
				const svgs = await fetchBodySvgs();
				setSvgContent({
					male: svgs.male_svg,
					female: svgs.female_svg,
					cell: svgs.cell_svg,
				});

				// Then fetch gene expression data
				const [organData, locationData] = await Promise.all([fetchGeneOrganExpressions(geneName), fetchCellularLocations(geneName)]);

				setOrganExpressions(organData);
				setCellularLocations(locationData);

				// Set sex to male by default
				setSex("M");
			} catch (err) {
				setError(`Error loading gene expression data: ${err instanceof Error ? err.message : String(err)}`);
				console.error("Error loading gene expression data:", err);
			} finally {
				setLoading(false);
			}
		};

		fetchData();
	}, [geneName]);

	// Apply colors to organs when organ expressions change
	useEffect(() => {
		if (organExpressions.length > 0 && svgContent.male && svgContent.female) {
			// Clear previous organ list
			if (organLevelsRef.current) {
				organLevelsRef.current.innerHTML = "";
			}

			// Create the organ list and apply colors
			renderOrganList();
		}
	}, [organExpressions, svgContent]);

	// Helper function to render the organ list and apply colors
	const renderOrganList = () => {
		// Get highest expression value for normalization
		const highestExpression = Math.max(
			...organExpressions.map((item) => {
				const key = Object.keys(item)[0];
				return item[key] || 0;
			})
		);

		// Process each organ
		organExpressions.forEach((item) => {
			const organ = Object.keys(item)[0];
			const expression = item[organ] || 0;

			// Skip organs with no expression
			if (expression <= 0) return;

			// Calculate opacity based on expression level
			const opacity = expression / highestExpression;
			const opacity2 = opacity / 2; // Lighter opacity for the list items

			// Get SVG element ID from organ name
			const organLoc = organParse(organ);

			// Create list item for the organ
			if (organLevelsRef.current) {
				const organLabel = document.createElement("a");
				organLabel.href = "#";
				organLabel.id = `_${organ}`;
				organLabel.className = "organ";
				organLabel.style.setProperty("--organ-level", String(opacity2));
				organLabel.setAttribute("data-organ", organLoc); // Store organ ID for later use
				organLabel.setAttribute("title", organ);
				organLabel.textContent = organLoc.replace(/_/g, " ");

				// Add hover interactions
				organLabel.addEventListener("mouseenter", () => {
					setActiveOrgan(organ);
					highlightOrgan(organLoc, true);
				});

				organLabel.addEventListener("mouseleave", () => {
					setActiveOrgan(null);
					highlightOrgan(organLoc, false);
				});

				organLevelsRef.current.appendChild(organLabel);
			}
		});
	};

	// Helper function to highlight/unhighlight organs on hover
	const highlightOrgan = (organId: string, isHighlight: boolean) => {
		// Use CSS selectors to find all elements containing the organ ID
		const organElements = document.querySelectorAll(`[id*="${organId}"]`);

		organElements.forEach((el) => {
			if (isHighlight) {
				// Highlight the organ
				(el as HTMLElement).style.stroke = "var(--mg-organ-outline-active)";
				(el as HTMLElement).style.strokeWidth = "1.5";
			} else {
				// Remove highlight
				(el as HTMLElement).style.stroke = "var(--mg-organ-outline)";
				(el as HTMLElement).style.strokeWidth = "0.5";
			}
		});
	};

	// Helper function to apply color to cell compartments based on location
	const highlightCellLocation = (locationText: string, isHighlight: boolean) => {
		if (!locationText) return;

		const organelleId = locationParse(locationText);
		if (organelleId) {
			const elementToHighlight = document.getElementById(organelleId);
			if (elementToHighlight) {
				elementToHighlight.style.fill = isHighlight ? "var(--mg-organ-location-active)" : "";
			}
		}
	};

	// Render loading skeleton when data is being fetched
	if (loading) {
		return (
			<div className={`organ-expression-container ${className}`}>
				<div className="organ-expression-loading">
					<Skeleton.Input active className="organ-expression-loading__line" />
					<Skeleton.Input active className="organ-expression-loading__line organ-expression-loading__line--wide" />
					<div className="organ-expression-loading__figure">
						<Skeleton.Avatar active size={240} shape="circle" className="organ-expression-loading__avatar" />
					</div>
					<Skeleton.Input active className="organ-expression-loading__line" />
					<Skeleton active paragraph={{ rows: 3 }} />
				</div>
			</div>
		);
	}

	// Render error message if there was an error
	if (error) {
		return (
			<div className={`organ-expression-container ${className}`}>
				<Alert message={error} type="error" showIcon className="organ-expression-alert" />
			</div>
		);
	}

	// If no gene is selected or no data is available
	if (!geneName || (organExpressions.length === 0 && cellularLocations.length === 0)) {
		return (
			<div className={`organ-expression-container ${className}`}>
				<Alert message="Select a gene to view its expression in organs and cells" type="info" showIcon className="organ-expression-alert" />
			</div>
		);
	}

	return (
		<div className={`organ-expression-container ${className}`}>
			{organExpressions.length > 0 ? (
				<div className="all-organs">
					{/* List of organs with expression */}
					<div id="organ-levels" ref={organLevelsRef} className="organ-list-container"></div>

					{/* Body visualization using dangerouslySetInnerHTML */}
					<div className="body-visualization-container">
						{/* Male body - visible by default */}
						<div id="male_organs" className={`body-viz${sex === "F" ? " body-viz--hidden" : ""}`} dangerouslySetInnerHTML={{ __html: svgContent.male }}></div>

						{/* Female body - hidden by default */}
						<div id="female_organs" className={`body-viz${sex === "F" ? "" : " body-viz--hidden"}`} dangerouslySetInnerHTML={{ __html: svgContent.female }}></div>
					</div>
				</div>
			) : (
				<Alert message="No organ expression data available for this gene" type="info" showIcon className="organ-expression-alert" />
			)}

			<Divider />

			{/* Cell Location Section */}
			<Text strong className="organ-expression-section-title">
				Activity in cells
			</Text>

			{cellularLocations.length > 0 ? (
				<div className="cell-visualization-container">
					{/* Cell visualization using dangerouslySetInnerHTML */}
					<div id="eu_cell" className="cell-viz" dangerouslySetInnerHTML={{ __html: svgContent.cell }}></div>

					{/* List of cellular locations */}
					<div className="location-container">
						{cellularLocations
							.filter((location) => location.aspect === "C") // 'C' for cellular component
							.map((location, index) => (
								<div
									key={`location-${index}`}
									className={`location-item ${activeLocation === location.terms_go_term ? "active" : ""}`}
									onMouseEnter={() => {
										setActiveLocation(location.terms_go_term);
										highlightCellLocation(location.terms_go_term, true);
									}}
									onMouseLeave={() => {
										setActiveLocation(null);
										highlightCellLocation(location.terms_go_term, false);
									}}
								>
									<span>{location.terms_go_term}</span>
									{location.terms_go_big_term && (
										<div className="location-detail">
											{location.terms_go_big_term}
										</div>
									)}
								</div>
							))}
					</div>
				</div>
			) : (
				<Alert message="No cellular location data available for this gene" type="info" showIcon className="organ-expression-alert" />
			)}
		</div>
	);
};

export default OrganExpressionVisualization;
